import {
  ScreenSizeController,
  screenSizeMap,
} from '../../controllers/screen-size-controller';
import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotsNGController as SlotsController } from '../../controllers/slots-ng-controller';
import componentStyles from './sic-nonprofit-introduction.css.lit';

import { wrapLastWord } from '../../../utilities/wrap-last-word';
import { externalLinkText } from '../../../utilities/external-link-text';

/**
 * The nonprofit introduction
 * @slot heading
 * @slot description
 * @slot logo
 * @slot website
 * @slot phone
 * @slot category
 * @slot social-icons (only add ones that they have an account of)
 *
 * Lightdom styles in sic-nonprofit-introduction.lightdom.css included in /shared.css
 */

@customElement('sic-nonprofit-introduction')
export class SicNonprofitIntroduction extends OutlineElement {
  slots = new SlotsController(this);
  screens = new ScreenSizeController(this);
  static styles: CSSResultGroup = [componentStyles];

  connectedCallback(): void {
    super.connectedCallback();

    // The purpose of this block of code is to attach external link icon to the last word.
    this.querySelectorAll('a').forEach(element => {
      const elementTarget = element?.getAttribute('target');
      if (elementTarget === '_blank') {
        // Wraps last word in a span.last-word element.
        // span.last-word is where the icon is added via pseudo
        !element?.querySelector('.last-word') &&
          wrapLastWord(element as HTMLElement);

        // Adds external link text for screen readers.
        externalLinkText(element as HTMLElement);
      }
    });
  }

  render(): TemplateResult {
    const isMobile = window.innerWidth < (screenSizeMap.get('lg') as number);
    const wrapperClasses = {
      'container': true,
      'is-mobile': isMobile,
    };

    return !isMobile
      ? html`
          <div class="${classMap(wrapperClasses)}">
            <div class="info">
              ${this.slots.conditionalSlot('heading')}
              ${this.slots.conditionalSlot('description', false)}
              <div class="links">
                ${this.slots.conditionalSlot('website', false)}
                ${this.slots.conditionalSlot('phone', false)}
              </div>
              ${this.slots.conditionalSlot('category', false)}
              ${this.slots.conditionalSlot('social-icons', false)}
            </div>
            ${this.slots.conditionalSlot('logo', false)}
          </div>
        `
      : html`
          <div class="${classMap(wrapperClasses)}">
            ${this.slots.conditionalSlot('logo', false)}
            <div class="info">
              ${this.slots.conditionalSlot('heading')}
              ${this.slots.conditionalSlot('description', false)}
              <div class="links">
                ${this.slots.conditionalSlot('website', false)}
                ${this.slots.conditionalSlot('phone', false)}
              </div>
              ${this.slots.conditionalSlot('category', false)}
              ${this.slots.slotHasContent('social-icons')
                ? html`
                    <div class="social-icons">
                      <slot name="social-icons"></slot>
                    </div>
                  `
                : null}
              <!-- ${this.slots.conditionalSlot('social-icons', false)} -->
            </div>
          </div>
        `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'sic-nonprofit-introduction': SicNonprofitIntroduction;
  }
}
